import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, FileText, CheckCircle, AlertCircle, Users, Building2, Calendar, TrendingUp, CalendarDays, Bell, BellRing } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { pushSupported, getExistingSubscription, subscribeToPush } from '../utils/push';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);

  useEffect(() => {
    fetchData();
    checkPushStatus();
  }, []);

  const checkPushStatus = async () => {
    try {
      if (!pushSupported() || Notification.permission === 'denied') return;
      if (localStorage.getItem('push_banner_dismissed') === '1') return;
      const sub = await getExistingSubscription();
      if (!sub) setShowPushBanner(true);
    } catch { /* ignora */ }
  };

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      await subscribeToPush();
      toast.success('Notifiche attivate! Riceverai un avviso per i nuovi lavori pianificati.');
      setShowPushBanner(false);
    } catch (e) {
      toast.error(e.message || 'Impossibile attivare le notifiche');
    } finally {
      setEnablingPush(false);
    }
  };

  const dismissPushBanner = () => {
    localStorage.setItem('push_banner_dismissed', '1');
    setShowPushBanner(false);
  };

  const fetchData = async () => {
    try {
      const [statsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/reports`)
      ]);
      setStats(statsRes.data);
      setRecentReports(reportsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-badge status-pending',
      approved: 'status-badge status-approved',
      rejected: 'status-badge status-rejected'
    };
    const labels = {
      pending: 'In Attesa',
      approved: 'Approvato',
      rejected: 'Rifiutato'
    };
    return <span className={styles[status]}>{labels[status]}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  // Format large numbers compactly
  const formatValue = (value, isHours = false) => {
    if (value === null || value === undefined) return '0';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    if (isHours) {
      return num.toFixed(2);
    }
    
    // For counts
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const kpiCards = [
    {
      title: 'Ore Totali Anno',
      value: formatValue(stats?.total_hours_year, true),
      rawValue: stats?.total_hours_year?.toFixed(2) || '0.00',
      icon: TrendingUp,
      color: 'text-rose-600',
      bgColor: 'bg-rose-100'
    },
    {
      title: 'Rapporti Anno',
      value: formatValue(stats?.total_reports_year),
      rawValue: stats?.total_reports_year || 0,
      icon: Calendar,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'Ore Totali Mese',
      value: formatValue(stats?.total_hours_month, true),
      rawValue: stats?.total_hours_month?.toFixed(2) || '0.00',
      icon: Clock,
      color: 'text-[#1B6B93]',
      bgColor: 'bg-[#1B6B93]/10'
    },
    {
      title: 'Rapporti Mese',
      value: formatValue(stats?.total_reports_month),
      rawValue: stats?.total_reports_month || 0,
      icon: FileText,
      color: 'text-[#4BA3C7]',
      bgColor: 'bg-[#4BA3C7]/10'
    },
    {
      title: 'In Attesa',
      value: formatValue(stats?.pending_reports),
      rawValue: stats?.pending_reports || 0,
      icon: AlertCircle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    },
    {
      title: 'Approvati',
      value: formatValue(stats?.approved_reports),
      rawValue: stats?.approved_reports || 0,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    }
  ];

  if (isAdmin()) {
    kpiCards.push(
      {
        title: 'Dipendenti',
        value: formatValue(stats?.total_employees),
        rawValue: stats?.total_employees || 0,
        icon: Users,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100'
      },
      {
        title: 'Cantieri',
        value: formatValue(stats?.total_sites),
        rawValue: stats?.total_sites || 0,
        icon: Building2,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100'
      }
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            Benvenuto, {user?.name}
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin() ? 'Panoramica amministrativa' : 'I tuoi rapporti straordinario'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-500">Matricola</p>
          <p className="font-mono text-lg font-semibold text-[#1B6B93]">{user?.matricola}</p>
        </div>
      </div>

      {/* Banner attivazione notifiche push */}
      {showPushBanner && (
        <Card className="border-l-4 border-l-amber-400 bg-amber-50/60" data-testid="push-banner">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <BellRing className="w-6 h-6 text-amber-500 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-slate-700">Attiva le notifiche</p>
              <p className="text-sm text-slate-500">Ricevi un avviso sul telefono quando arriva un nuovo lavoro pianificato</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" onClick={handleEnablePush} disabled={enablingPush} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="enable-push-btn">
                <Bell className="w-4 h-4 mr-1" /> {enablingPush ? 'Attivo...' : 'Attiva'}
              </Button>
              <Button size="sm" variant="ghost" onClick={dismissPushBanner} className="text-slate-500" data-testid="dismiss-push-btn">
                Non ora
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Mobile prominent, Desktop subtle */}
      <div className="md:hidden">
        <Card className="bg-gradient-to-r from-[#1B6B93] to-[#4BA3C7] text-white shadow-lg">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-3">Azioni Rapide</h3>
            <div className="grid grid-cols-2 gap-3">
              <a 
                href="/reports?new=single" 
                className="flex flex-col items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors"
              >
                <FileText className="w-8 h-8 mb-2" />
                <span className="text-sm font-medium text-center">Nuovo Rapporto</span>
              </a>
              <a 
                href="/reports?new=multi" 
                className="flex flex-col items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors"
              >
                <CalendarDays className="w-8 h-8 mb-2" />
                <span className="text-sm font-medium text-center">Rapporto Multigiorno</span>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="kpi-card animate-fade-in overflow-hidden" data-testid={`kpi-card-${index}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-500 mb-1">{kpi.title}</p>
                  <p 
                    className="text-2xl md:text-3xl font-bold text-slate-800" 
                    title={kpi.rawValue !== undefined ? `${kpi.rawValue}` : kpi.value}
                  >
                    {kpi.value}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${kpi.bgColor} flex items-center justify-center flex-shrink-0 ml-2`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Reports */}
      <Card className="shadow-sm" data-testid="recent-reports-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800">
            Rapporti Recenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentReports.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              Nessun rapporto presente
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    {isAdmin() && <th>Dipendente</th>}
                    <th>Cantiere</th>
                    <th>Ore</th>
                    <th>Attività</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReports.map((report) => (
                    <tr key={report.id} data-testid={`report-row-${report.id}`}>
                      <td className="font-medium">{report.date}</td>
                      {isAdmin() && <td>{report.employee_name}</td>}
                      <td>{report.site_name}</td>
                      <td className="font-mono">{report.total_hours.toFixed(2)}h</td>
                      <td className="max-w-[200px] truncate">{report.activity_type}</td>
                      <td>{getStatusBadge(report.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
